import * as React from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import axios from 'axios';
import { withTranslation } from 'react-i18next';

import { SearchInput } from '../shared/ListFilters';
import useCalendarState from './hooks/useCalendarState';
import { format, parseTimeToHour } from './hooks/dateUtils';
import { FILTER_NONE } from './calendarConstants';

const MIN_QUERY_LENGTH = 2;

// "23:59" rather than "24:00": the hour pickers in the filter panel show a real clock time.
const formatHour = (hour) => (hour >= 24 ? '23:59' : `${String(hour).padStart(2, '0')}:00`);

/**
 * The hours a booking occupies on the day it starts. A booking that runs past midnight
 * reports an end hour on the *next* day (20:15 -> 00:15), so its end has to be read as
 * end-of-day or every calculation below silently inverts.
 */
const occupiedHours = (result) => {
  const start = result.local_start_hour;
  if (start === null || start === undefined) return null;
  const spansMidnight = result.local_end_date && result.local_end_date !== result.local_start_date;
  const end = spansMidnight ? 24 : (result.local_end_hour ?? start + 1);
  return { start, end: Math.max(end, start) };
};

/**
 * Work out whether the calendar would hide a match we just navigated to. Without this the
 * calendar silently appears to do nothing.
 * Returns null when the match is visible, otherwise a key describing what hides it.
 */
const findHiddenReason = (result) => {
  const state = useCalendarState.getState();
  const { filters, calendarMode, currentView } = state;

  if (!result.is_scheduled) return null;

  if (filters.states.length > 0 && !filters.states.includes(result.state)) {
    return 'states';
  }

  if (filters.employees.length > 0) {
    const employeeIds = result.employee_ids || [];
    const visible = employeeIds.length === 0
      ? filters.employees.includes(FILTER_NONE)
      : employeeIds.some((id) => filters.employees.includes(id));
    if (!visible) return 'employees';
  }

  if (filters.serviceBays.length > 0) {
    const visible = result.service_bay_id
      ? filters.serviceBays.includes(result.service_bay_id)
      : filters.serviceBays.includes(FILTER_NONE);
    if (!visible) return 'serviceBays';
  }

  // Week mode only renders the visible weekdays; day mode always renders its own day.
  if (calendarMode === 'week') {
    const visibleDates = state.getWeekDates().map((date) => format(date, 'yyyy-MM-dd'));
    if (!visibleDates.includes(result.local_start_date)) return 'day';
  }

  // Both grids key events by resource: in the employee view a booking with nobody
  // assigned lands in no column at all, same for a bay-less booking in the bay view.
  if (currentView === 'employee' && !(result.employee_ids || []).length) return 'view';
  if (currentView === 'bay' && !result.service_bay_id) return 'view';

  // Hidden only when the booking falls entirely outside the visible hours — one that
  // merely starts early or ends late is still drawn, clamped to the window.
  const hours = occupiedHours(result);
  if (hours) {
    const { dayStart, dayEnd } = state.getEffectiveWorkHours();
    if (hours.start >= parseTimeToHour(dayEnd, 16) || hours.end <= parseTimeToHour(dayStart, 8)) {
      return 'hours';
    }
  }

  return null;
};

function CalendarSearch({ t }) {
  const {
    searchResults,
    searchIndex,
    searchLoading,
    searchError,
    searchNotFound,
    setSearchLoading,
    setSearchError,
    setSearchResults,
    clearSearch,
    resetSearchFields,
    applySearchResult,
  } = useCalendarState();

  const [queryDraft, setQueryDraft] = React.useState('');
  const [hiddenReason, setHiddenReason] = React.useState(null);
  // Bumped by every search, by clearing the box and by leaving the calendar: an answer that
  // comes back after any of them belongs to nobody and must not move the calendar or fill the box.
  const searchSeqRef = React.useRef(0);

  // Leaving the calendar should not leave a stale search behind: its error, spinner or
  // results always go, its highlight only when a result was navigated to. A highlight from a
  // "View in calendar" link is not the search's, and StrictMode's extra unmount would
  // otherwise wipe it the moment the calendar opens.
  React.useEffect(() => () => {
    searchSeqRef.current += 1;
    if (useCalendarState.getState().searchIndex >= 0) {
      clearSearch();
    } else {
      resetSearchFields();
    }
  }, [clearSearch, resetSearchFields]);

  const goToResult = React.useCallback((index) => {
    applySearchResult(index);
    const result = useCalendarState.getState().searchResults[index];
    setHiddenReason(result ? findHiddenReason(result) : null);
  }, [applySearchResult]);

  const runSearch = React.useCallback(async (rawTerm) => {
    const term = (rawTerm || '').trim();
    setHiddenReason(null);

    if (term.length < MIN_QUERY_LENGTH) {
      searchSeqRef.current += 1;
      clearSearch();
      return;
    }

    // Same term again: step to the next match instead of re-querying.
    const { searchQuery: previousQuery, searchResults: previousResults, searchIndex: previousIndex } =
      useCalendarState.getState();
    if (term === previousQuery && previousResults.length > 0) {
      goToResult((previousIndex + 1) % previousResults.length);
      return;
    }

    searchSeqRef.current += 1;
    const seq = searchSeqRef.current;
    setSearchLoading(true);
    try {
      const response = await axios.get('/api/scheduling/calendar/search/', { params: { q: term } });
      if (seq !== searchSeqRef.current) return;
      const results = response.data?.results || [];
      setSearchResults(term, results);
      if (results.length > 0) {
        goToResult(Math.min(response.data?.initial_index || 0, results.length - 1));
      }
    } catch (err) {
      if (seq !== searchSeqRef.current) return;
      console.error('Calendar search failed:', err);
      setSearchError(t('Search failed'));
    }
  }, [clearSearch, goToResult, setSearchError, setSearchLoading, setSearchResults, t]);

  const handleClear = React.useCallback(() => {
    setHiddenReason(null);
    setQueryDraft('');
    searchSeqRef.current += 1;
    clearSearch();
  }, [clearSearch]);

  const handleShowNext = React.useCallback(() => {
    if (searchResults.length === 0) return;
    goToResult((searchIndex + 1) % searchResults.length);
  }, [goToResult, searchIndex, searchResults.length]);

  // Clear one blocker. Kept separate from the loop below so that a match hidden for
  // several reasons at once still takes a single click to reveal.
  const clearBlocker = (reason, result) => {
    const state = useCalendarState.getState();

    if (reason === 'day') {
      const [year, month, day] = (result.local_start_date || '').split('-').map(Number);
      if (!year) return false;
      state.switchToDay(new Date(year, month - 1, day));
      return true;
    }

    if (reason === 'view') {
      // Nothing to widen: an unassigned booking only has a column in the combined view.
      state.setCurrentView('all');
      return true;
    }

    if (reason === 'hours') {
      // Widen from the window actually in effect, never from the org default — otherwise
      // this would quietly throw away the user's own narrower hours.
      const hours = occupiedHours(result);
      if (!hours) return false;
      const effective = state.getEffectiveWorkHours();
      const orgStart = parseTimeToHour(state.workSchedule.dayStart, 8);
      const orgEnd = parseTimeToHour(state.workSchedule.dayEnd, 16);
      const start = Math.min(parseTimeToHour(effective.dayStart, 8), Math.max(0, Math.floor(hours.start)));
      const end = Math.max(parseTimeToHour(effective.dayEnd, 16), Math.min(24, Math.ceil(hours.end)));
      state.updateFilters({
        visibleStartHour: start === orgStart ? null : formatHour(start),
        visibleEndHour: end === orgEnd ? null : formatHour(end),
      });
      return true;
    }

    if (reason) {
      state.updateFilters({ [reason]: [] });
      return true;
    }

    return false;
  };

  // Escape hatch: "Näytä silti" means show it whatever it takes, so keep clearing
  // blockers until the match would actually render.
  const handleShowAnyway = React.useCallback(() => {
    const result = useCalendarState.getState().searchResults[useCalendarState.getState().searchIndex];
    if (!result) return;

    let reason = hiddenReason;
    for (let guard = 0; reason && guard < 6; guard += 1) {
      if (!clearBlocker(reason, result)) break;
      reason = findHiddenReason(result);
    }

    setHiddenReason(reason);
  }, [hiddenReason]);

  const currentResult = searchResults[searchIndex];
  const hasResults = searchResults.length > 0;
  // Nothing runs until Enter (auto-jumping mid-keystroke would yank the calendar around).
  const showEnterHint = queryDraft.trim().length > 0 && !hasResults && !searchLoading
    && !searchNotFound && !searchError;

  let message = null;
  if (searchError) {
    message = searchError;
  } else if (searchNotFound) {
    message = t('No matches');
  } else if (hiddenReason === 'view') {
    message = t('Match is not visible in this view');
  } else if (hiddenReason) {
    message = t('Match is hidden by filters');
  } else if (currentResult && !currentResult.is_scheduled) {
    message = t('Not scheduled yet');
  }

  return (
    <Box data-testid="calendar-search" sx={{ width: '100%' }}>
      {/* Field and result controls wrap onto their own line on narrow screens */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: '1 1 280px', minWidth: 0, maxWidth: { xs: '100%', md: 520 } }}>
          <SearchInput
            value={queryDraft}
            onChange={setQueryDraft}
            onEnter={runSearch}
            onClear={handleClear}
            placeholder={t('Search by registration, work number or customer')}
            maxWidth="100%"
            data-testid="calendar-search-input"
          />
        </Box>

        {(searchLoading || hasResults) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {searchLoading && <CircularProgress size={18} />}
            {hasResults && (
              <Chip
                label={`${searchIndex + 1} / ${searchResults.length}`}
                size="small"
                data-testid="calendar-search-counter"
                sx={{ fontWeight: 600 }}
              />
            )}
            {searchResults.length > 1 && (
              <Button
                size="small"
                onClick={handleShowNext}
                startIcon={<SkipNextIcon />}
                data-testid="calendar-search-next"
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {t('Show next')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {showEnterHint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {t('Press Enter to search')}
        </Typography>
      )}

      {message && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <Typography
            variant="caption"
            color={searchError ? 'error.main' : 'text.secondary'}
            data-testid="calendar-search-message"
          >
            {message}
          </Typography>
          {hiddenReason && (
            <Button
              size="small"
              onClick={handleShowAnyway}
              sx={{ textTransform: 'none', py: 0, minWidth: 0, fontSize: '12px' }}
            >
              {t('Show anyway')}
            </Button>
          )}
        </Box>
      )}

      {currentResult && currentResult.is_scheduled && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.75 }}
          data-testid="calendar-search-current"
        >
          {[currentResult.vehicle_registration, currentResult.work_number ? `#${currentResult.work_number}` : null,
            currentResult.customer_name].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </Box>
  );
}

export default withTranslation()(CalendarSearch);
